// Die Schilder an den Toren: je Parzelle ein Brett mit dem Namen des Besitzers, gemalt auf ein
// Canvas und als Textur auf eine flache Tafel gelegt, die zum Weg blickt
// (docs/architektur-paket-2.md, Abschnitt „Welt mit Parzellen“, ADR 0025).
//
// Warum eine feste Tafel und kein mitdrehendes Sprite: Der Schildblock steht in der Zaunlinie
// unmittelbar neben dem Tor (ADR 0026). Ein Sprite würde sich zum Spieler drehen und dabei durch
// Pfosten und Zaun schneiden; die feste Tafel bleibt am Brett und ist von dort lesbar, wo man
// ohnehin steht – auf dem Weg vor dem Tor. Sie hängt über der Zaunkrone, nicht davor.
//
// Der Besitzer kommt aus `hello.parcels` und wird über `parcelOwner` nachgezogen; in den
// Three.js-Objekten liegt kein Spielzustand, nur der zuletzt gemalte Text (Architektur-Regel 1).

import {
  CanvasTexture,
  DoubleSide,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
  type Scene,
} from 'three';
import { CHUNK_SIZE, type Parcel, type ParcelInfo, type Vec3 } from '@bauernhof/shared';
import { SICHTWEITE, chunkAbstand, type Grundriss } from './sichtweite.js';

/** Maße des Schildes: Tafel in Blöcken, Malfläche in Pixeln. */
export const SCHILD = {
  /** So viele Zeichen passen aufs Brett; längere Namen werden mit „…“ gekürzt. */
  maxZeichen: 16,
  /** Mitte der Tafel über dem Fußpunkt des Schildblocks, in Blöcken. */
  hoeheUeberBoden: 1.55,
  /** Breite und Höhe der Tafel in Blöcken. */
  breite: 1.6,
  hoehe: 0.5,
  /** Auflösung der Malfläche; ein glatter Faktor der Tafelmaße. */
  pixelBreite: 256,
  pixelHoehe: 80,
} as const;

/** Farben des Brettes: helles Holz mit dunklem Rand und dunkler Schrift. */
const FARBEN = { holz: '#c9a86a', rand: '#6b4a24', schrift: '#3a2412' } as const;

/** Der Text auf dem Brett: der Name des Besitzers, gekürzt; ein freies Schild bleibt leer. */
export function schildText(owner: string | null): string {
  const name = (owner ?? '').trim();
  if (name === '') return '';
  if (name.length <= SCHILD.maxZeichen) return name;
  return `${name.slice(0, SCHILD.maxZeichen - 1)}…`;
}

/** Mitte der Tafel: über dem Schildblock, in der Mitte seiner Zelle. */
export function schildPunkt(parcel: Parcel): Vec3 {
  return { x: parcel.sign.x + 0.5, y: parcel.sign.y + SCHILD.hoeheUeberBoden, z: parcel.sign.z + 0.5 };
}

/**
 * Blickrichtung der Tafel als `yaw` nach der Regel des Spiels (Blickvektor `(-sin, -cos)`,
 * 0 = Norden): Sie schaut in die Richtung, in der auch das Tor liegt, also zum Weg.
 */
export function schildBlick(parcel: Parcel): number {
  switch (parcel.sign.facing) {
    case 'n':
      return 0;
    case 'w':
      return Math.PI / 2;
    case 's':
      return Math.PI;
    case 'e':
      return -Math.PI / 2;
  }
}

/** Die Schilder als Ansicht: Besitzer setzen, je Bild die Sichtweite nachziehen, aufräumen. */
export interface Schilder {
  /** Setzt den Besitzer einer Parzelle; `null` macht das Schild wieder leer. */
  setBesitzer(id: number, owner: string | null): void;
  /** Setzt alle Besitzer auf einmal, so wie `hello.parcels` sie liefert. */
  setzeAlle(liste: readonly ParcelInfo[]): void;
  /** Zeigt nur die Schilder innerhalb der Sichtweite; je Bild aufzurufen. */
  sync(kamera: Grundriss): void;
  dispose(): void;
}

/** Ein Schild in der Szene: Tafel, eigene Textur und der zuletzt gemalte Text. */
interface SchildAnsicht {
  mesh: Mesh;
  texture: CanvasTexture;
  material: MeshBasicMaterial;
  zeichne(text: string): void;
  text: string;
  /** Chunk-Koordinaten des Schildes, für die Sichtweite. */
  cx: number;
  cz: number;
}

/** Hängt für jede Parzelle ein Schild in die Szene. Alle Bretter teilen sich eine Geometrie. */
export function createSchilder(scene: Scene, parcels: readonly Parcel[]): Schilder {
  const geometry = new PlaneGeometry(SCHILD.breite, SCHILD.hoehe);
  const schilder = new Map<number, SchildAnsicht>();

  for (const parcel of parcels) {
    const ansicht = baueSchild(geometry, parcel);
    scene.add(ansicht.mesh);
    schilder.set(parcel.id, ansicht);
  }

  const setBesitzer = (id: number, owner: string | null): void => {
    const ansicht = schilder.get(id);
    if (ansicht === undefined) return;
    const text = schildText(owner);
    if (text === ansicht.text) return;
    ansicht.zeichne(text);
  };

  return {
    setBesitzer,

    setzeAlle(liste) {
      for (const eintrag of liste) setBesitzer(eintrag.id, eintrag.owner);
    },

    sync(kamera) {
      for (const ansicht of schilder.values()) {
        ansicht.mesh.visible = chunkAbstand(ansicht.cx, ansicht.cz, kamera) <= SICHTWEITE;
      }
    },

    dispose() {
      for (const ansicht of schilder.values()) {
        scene.remove(ansicht.mesh);
        ansicht.texture.dispose();
        ansicht.material.dispose();
      }
      schilder.clear();
      geometry.dispose();
    },
  };
}

function baueSchild(geometry: PlaneGeometry, parcel: Parcel): SchildAnsicht {
  const canvas = document.createElement('canvas');
  canvas.width = SCHILD.pixelBreite;
  canvas.height = SCHILD.pixelHoehe;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Das Schild braucht einen 2d-Kontext; der Browser gab keinen her.');

  const texture = new CanvasTexture(canvas);
  // Die Schrift ist fein; ohne weiche Filterung flimmert sie aus der Ferne.
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.colorSpace = SRGBColorSpace;

  const material = new MeshBasicMaterial({ map: texture, side: DoubleSide, transparent: false });
  const mesh = new Mesh(geometry, material);
  const punkt = schildPunkt(parcel);
  mesh.position.set(punkt.x, punkt.y, punkt.z);
  // Die Tafel schaut nach +z, `yaw` blickt nach −z: eine halbe Drehung dazwischen.
  mesh.rotation.y = schildBlick(parcel) + Math.PI;
  mesh.matrixAutoUpdate = false;
  mesh.updateMatrix();

  const ansicht: SchildAnsicht = {
    mesh,
    texture,
    material,
    text: '',
    cx: Math.floor(punkt.x / CHUNK_SIZE),
    cz: Math.floor(punkt.z / CHUNK_SIZE),
    zeichne(text: string) {
      malSchild(ctx, text);
      texture.needsUpdate = true;
      ansicht.text = text;
    },
  };
  ansicht.zeichne('');
  return ansicht;
}

/** Malt Brett und Beschriftung; ein leeres Brett bleibt ohne Text. */
export function malSchild(ctx: CanvasRenderingContext2D, text: string): void {
  const { pixelBreite: b, pixelHoehe: h } = SCHILD;
  ctx.clearRect(0, 0, b, h);
  ctx.fillStyle = FARBEN.holz;
  ctx.fillRect(0, 0, b, h);
  ctx.strokeStyle = FARBEN.rand;
  ctx.lineWidth = 6;
  ctx.strokeRect(3, 3, b - 6, h - 6);
  if (text === '') return;

  ctx.fillStyle = FARBEN.schrift;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Der Schriftgrad schrumpft mit der Länge, damit auch ein langer Name aufs Brett passt.
  const grad = Math.min(44, Math.floor(((b - 24) * 1.8) / Math.max(text.length, 1)));
  ctx.font = `bold ${grad}px system-ui, sans-serif`;
  ctx.fillText(text, b / 2, h / 2 + 2, b - 24);
}
